import { Endpoint } from "shared/utils/endpoints";
import { HTTP_CLIENT } from "../utils/interceptor";

const SCHOOL_STAT = Endpoint.project_head.school_stat;

/** Every school-stat endpoint accepts the same optional Y-m-d window. */
export interface StatRange {
  from?: string;
  to?: string;
}

/** Builds `?from=&to=` plus any extras, skipping empty values. */
const withRange = (
  path: string,
  range?: StatRange,
  extra?: Record<string, string | number | undefined>
) => {
  const params = new URLSearchParams();
  if (range?.from) params.append("from", range.from);
  if (range?.to) params.append("to", range.to);
  Object.entries(extra ?? {}).forEach(([key, value]) => {
    if (value !== undefined && value !== "") params.append(key, String(value));
  });
  const query = params.toString();
  return query ? `${path}?${query}` : path;
};

/** Totals to date: schools, campuses, seats, classrooms. Takes no window. */
const getOverallStatService = () => HTTP_CLIENT.get(SCHOOL_STAT.overallStat);

/** The six account counts with their trend against the previous window. */
const getSchoolDashboardService = (range?: StatRange) =>
  HTTP_CLIENT.get(withRange(SCHOOL_STAT.dashboard, range));

/**
 * Paginated school list. `type`: demo | test | premium | expired.
 * `status`: active | expired | paused. `search` matches the school's name.
 */
const getReaderSchoolsService = (
  range?: StatRange,
  type?: string,
  page = 1,
  perPage = 10,
  status?: string,
  search?: string
) =>
  HTTP_CLIENT.get(
    withRange(SCHOOL_STAT.readerSchools, range, {
      type: type && type !== "all" ? type : undefined,
      status: status && status !== "all" ? status : undefined,
      search: search || undefined,
      page,
      per_page: perPage,
    })
  );

/** Schools split into healthy / slipping / dead, with each band's schools. */
const getHealthStatService = (range?: StatRange) =>
  HTTP_CLIENT.get(withRange(SCHOOL_STAT.healthStat, range));

/** Seats purchased → enrolled → page openers → 3+ book readers. */
const getSeatFunnelService = (range?: StatRange) =>
  HTTP_CLIENT.get(withRange(SCHOOL_STAT.seatFunnel, range));

/** Schools where no teacher assigned a book inside the window. */
const getNobodyAssigningService = (range?: StatRange) =>
  HTTP_CLIENT.get(withRange(SCHOOL_STAT.nobodyAssigning, range));

/** Completed books split by genre, language, age group, source, publisher. */
const getReadingMixService = (
  range?: StatRange,
  types = "genre,language,age_group,source,publisher"
) => HTTP_CLIENT.get(withRange(SCHOOL_STAT.readingMix, range, { types }));

/** Top 5 books. `type`: by_read | by_complete. */
const getTopBooksService = (range?: StatRange, type = "by_read") =>
  HTTP_CLIENT.get(withRange(SCHOOL_STAT.topBooks, range, { type }));

/** Bucketed trend. `type`: book_read | cumulative_reading | engagement. */
const getReadingGrowthTrendService = (range?: StatRange, type = "book_read") =>
  HTTP_CLIENT.get(withRange(SCHOOL_STAT.readingGrowthTrend, range, { type }));

/** Students who read on every day of the window. */
const getRegularReaderService = (range?: StatRange) =>
  HTTP_CLIENT.get(withRange(SCHOOL_STAT.regularReader, range));

/** Assigned-book counts by status: not started, in progress, completed, overdue. */
const getAssignmentCountService = (range?: StatRange) =>
  HTTP_CLIENT.get(withRange(SCHOOL_STAT.assignmentCount, range));

/** Students who completed at least one reading level in the window. */
const getReadingLevelService = (range?: StatRange) =>
  HTTP_CLIENT.get(withRange(SCHOOL_STAT.readingLevel, range));

export {
  getOverallStatService,
  getSchoolDashboardService,
  getReaderSchoolsService,
  getHealthStatService,
  getSeatFunnelService,
  getNobodyAssigningService,
  getReadingMixService,
  getTopBooksService,
  getReadingGrowthTrendService,
  getRegularReaderService,
  getReadingLevelService,
  getAssignmentCountService,
};
